import { useState, useEffect, useCallback } from 'react';
import { Archive, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

const PAGE = 50;

// Read-only list of kitchen tickets older than 24h — nothing is closed or changed here.
export const OlderKitchenOrders = ({ branchId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });

  const load = useCallback(async (s = 0, query = q) => {
    const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
    if (!token) return;
    const p = new URLSearchParams({ kitchen: '1', scope: 'older', skip: String(s), limit: String(PAGE) });
    if (branchId) p.append('branch_id', branchId);
    if (query.trim()) p.append('q', query.trim());
    try {
      const res = await api.get(`/cashier/orders?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data || { items: [], total: 0 });
      setSkip(s);
    } catch (e) { console.error('older kitchen orders', e); }
  }, [branchId, q]);

  useEffect(() => { load(0, ''); }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setOpen(true); load(skip); }}
        className="border-stone-600 text-muted-foreground text-xs" data-testid="kds-older-orders-btn">
        <Archive size={16} className="mr-1.5" />Older Orders ({data.total})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="kds-older-orders-dialog">
          <DialogHeader><DialogTitle>Older kitchen orders (closed / served) — view only</DialogTitle></DialogHeader>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); load(0); }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order #, customer or table"
              data-testid="kds-older-search-input" />
            <Button type="submit" data-testid="kds-older-search-btn"><Search size={16} /></Button>
          </form>
          <div className="overflow-y-auto flex-1 text-sm" data-testid="kds-older-list">
            {data.items.length === 0 ? <p className="py-6 text-center text-muted-foreground">No older orders</p> : (
              <table className="w-full">
                <thead><tr className="text-left text-muted-foreground"><th>Order</th><th>Placed</th><th>Scheduled</th><th>Kitchen</th><th>Items</th></tr></thead>
                <tbody>
                  {data.items.map((o) => (
                    <tr key={o.id} className="border-t border-border" data-testid={`kds-older-row-${o.id}`}>
                      <td className="py-1.5 font-semibold">#{o.order_number}</td>
                      <td>{(o.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td>{(o.scheduled_for || '—').slice(0, 16).replace('T', ' ')}</td>
                      <td>{o.kitchen_status === 'auto_closed' ? 'Closed by system' : (o.kitchen_status || o.status)}</td>
                      <td>{(o.items || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="kds-older-range">{data.total ? `${skip + 1}–${Math.min(skip + PAGE, data.total)} of ${data.total}` : '0'}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => load(Math.max(0, skip - PAGE))} data-testid="kds-older-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={skip + PAGE >= data.total} onClick={() => load(skip + PAGE)} data-testid="kds-older-next">Next</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
